import React, { useState } from 'react'
import { BasicInput } from '@src/components/commons/input/formInput/basicInput'
import { BasicSelect } from '@src/components/commons/input/formInput/basicSelect'
import {
  PARTNERSHIP_CONSULT_OPTION_EN,
  PARTNERSHIP_CONSULT_OPTION_JP,
  PARTNERSHIP_CONSULT_OPTION_KR,
} from '../../constants'
import { BasicTextArea } from '@src/components/commons/input/formInput/basicTextArea'
import BasicButton from '@src/components/commons/button/BasicButton/BasicButton'
import { IUserInputData, createInputField, isInputValid, inputValidation } from '@src/utils/input/validation'
import { phoneMaxNumber, phoneNumberInputFormat } from '@src/utils/inputFilter'
import { wevPartnershipApis } from '../../api'
import { toast } from 'react-toastify'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { Toast_Warning } from '@src/assets/icons'
import WevAlertModal from '@src/components/commons/modal/wevAlertModal/WevAlertModal'
import useReduxModal from '@src/hooks/useModal'
import * as S from './Consult.styled'

const Consult = () => {
  const router = useRouter()
  const modal = useReduxModal()
  const { t } = useTranslation('partnership')
  const locale = router.locale || 'ko'
  const [consultType, setConsultType] = useState('PRODUCT_PURCHASE')
  const [userInputData, setUserInputData] = useState<IUserInputData>({
    name: createInputField('', true),
    phoneNumber: createInputField('', true),
    company: createInputField('', true),
    companyUrl: createInputField('', false),
    email: createInputField('', true),
    content: createInputField('', false),
  })

  const getLocaleData = (language: string) => {
    switch (language) {
      case 'en':
        return {
          data: PARTNERSHIP_CONSULT_OPTION_EN,
        }
      case 'jp':
        return {
          data: PARTNERSHIP_CONSULT_OPTION_JP,
        }
      default: // 한국어(kr)를 기본으로 설정
        return {
          data: PARTNERSHIP_CONSULT_OPTION_KR,
        }
    }
  }

  const inputChange = (field: string, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    event.preventDefault()
    const data = {
      ...userInputData,
      [field]: {
        isValid: true,
        errorMsg: '',
        value: event.target.value,
        required: ['companyUrl', 'content'].includes(field) ? false : true,
      },
    }
    setUserInputData(data)
  }

  const inputEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    const data: IUserInputData = {
      ...userInputData,
      email: { ...inputValidation('email', event.target.value), required: true },
    }
    setUserInputData(data)
  }

  const inputPhoneNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    const data: IUserInputData = {
      ...userInputData,
      phoneNumber: { ...inputValidation('phoneNumber', phoneNumberInputFormat(event.target.value)), required: true },
    }
    setUserInputData(data)
  }
  const registerConsult = async () => {
    const data = {
      inquiryType: consultType,
      companyName: userInputData.company.value as string,
      contactName: userInputData.name.value as string,
      contactPhone: (userInputData.phoneNumber.value as string).replaceAll('-', ''),
      contactEmail: userInputData.email.value as string,
      companyWebsite: userInputData.companyUrl.value as string,
      inquiryDetails: userInputData.content.value as string,
    }
    try {
      await wevPartnershipApis.partnershipConsult(data).then((res) => {
        if (res.data.resultCode === 'OK') {
          modal.openModal({
            component: () => (
              <WevAlertModal
                type="success"
                title={`${t('consult.success')}`}
                desc={`${t('consult.success_desc')}`}
                buttonText={t('consult.확인')}
                confirmFunc={() => {
                  setConsultType('PRODUCT_PURCHASE')
                  setUserInputData({
                    name: createInputField('', true),
                    phoneNumber: createInputField('', true),
                    company: createInputField('', true),
                    companyUrl: createInputField('', false),
                    email: createInputField('', true),
                    content: createInputField('', false),
                  })
                }}
              />
            ),
          })
        }
      })
    } catch (err: any) {
      toast.dark(err.response.data.result_message || `${t('consult.error_desc')}`, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2500,
        hideProgressBar: true,
        closeButton: false,
        icon: <Toast_Warning />,
      })
    }
  }

  const onClickSubmitButton = () => {
    registerConsult()
  }

  return (
    <S.Main>
      <div>
        <S.Title>{t('consult.title')}</S.Title>
        <S.Desc>{t('consult.desc')}</S.Desc>
        <span>*{t('consult.필수입력항목')}</span>
        <S.FormWrapper>
          <div>
            <div className="w-full flex flex-col gap-[20px]">
              <BasicSelect
                tag={`${t('consult.문의 유형')}`}
                options={getLocaleData(locale).data}
                onChange={(e) => setConsultType(e.target.value)}
                value={consultType}
              />
              <BasicInput
                tag={`${t('consult.name')}`}
                isPrimary
                placeholder={`${t('consult.name_placeholder')}`}
                onChange={(e) => inputChange('name', e)}
                value={userInputData.name.value}
              />

              <BasicInput
                tag={`${t('consult.phone')}`}
                isPrimary
                placeholder={`${t('consult.phone_placeholder')}(-{${t('consult.제외')})`}
                onChange={(e) => inputPhoneNumber(e)}
                value={userInputData.phoneNumber.value}
                maxLength={phoneMaxNumber(userInputData.phoneNumber.value as string)}
                error={userInputData.phoneNumber.errorMsg}
                isErrorLine={!userInputData.phoneNumber.isValid}
              />
            </div>
            <div className="w-full flex flex-col gap-[20px]">
              <BasicInput
                tag={`${t('consult.company')}`}
                isPrimary
                placeholder={`${t('consult.company_placeholder')}`}
                onChange={(e) => inputChange('company', e)}
                value={userInputData.company.value}
              />
              <BasicInput
                tag={`${t('consult.homepage')}`}
                placeholder={`${t('consult.homepage_placeholder')}`}
                onChange={(e) => inputChange('companyUrl', e)}
                value={userInputData.companyUrl.value}
              />
              <BasicInput
                tag={`${t('consult.email')}`}
                placeholder={`${t('consult.email_placeholder')}`}
                onChange={(e) => inputEmail(e)}
                value={userInputData.email.value}
                isPrimary
                error={userInputData.email.errorMsg}
                isErrorLine={!userInputData.email.isValid}
              />
            </div>
          </div>
          <div>
            <BasicTextArea
              tag={`${t('consult.detail')}`}
              placeholder={`${t('consult.detail_placeholder')}`}
              onChange={(e) => inputChange('content', e)}
              value={userInputData.content.value}
            />
          </div>
          <BasicButton
            color="Filled"
            style={{ maxWidth: '410px' }}
            onClick={onClickSubmitButton}
            disabled={!isInputValid(userInputData)}
          >
            {t('consult.제휴문의')}
          </BasicButton>
        </S.FormWrapper>
      </div>
    </S.Main>
  )
}

export default Consult
